import { useEffect } from 'react';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useIsFetching } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';
import { ExternalLink } from '@app/components';
import { languageOptions, passwordRegEx } from '@app/constants';
import { paths } from '@app/constants/paths.constant';
import {
  changeLanguage,
  getCurrentAppTheme,
  getRestorablePath,
  useElementWidth,
  useHydration,
} from '@app/utils';
import { useTokenData, useTokenUserData } from '@data/hooks';
import { Role, TokenType } from '@data/sdk/match';
import { useAppUserStore } from '@data/storage';
import { isValidEmail } from '@data/utils/validation';
import {
  AntdConfigProvider,
  AntdDivider,
  AntdForm,
  AntdGrid,
  AntdSelect,
  AntdTypography,
  Button,
  Card,
  isYWC,
  useTheme,
  YWCLogoSmall,
} from '@ui';
import { Input, InputPassword } from '../../components';
import { GoogleSignInButton } from './components';
import { useEmailVerification, useInterruptedSignUp, useSignIn } from './hooks';

import './sign-in.css';

const { useBreakpoint } = AntdGrid;
const { Text, Title } = AntdTypography;

const SignInPage = () => {
  const navigate = useNavigate();
  const { search, state } = useLocation();
  const { sm, md } = useBreakpoint();
  const isFetching = !!useIsFetching();
  const { i18n, t } = useTranslation();
  const { bgColor, baseSelect, textColor, textColorDark } = useTheme();
  const { elementRef, elementWidth } = useElementWidth<HTMLDivElement>(200);

  const hasHydrated = useHydration();
  const { user } = useAppUserStore();
  const [token] = useQueryParam('token', StringParam);
  const { data: tokenData } = useTokenData(token ?? '');
  const { userId: currentUserId } = useTokenUserData();

  useEffect(() => {
    if (hasHydrated && user) {
      // Covers sign-in flows that land here with a session already in the store — SSO, and a
      // session restored mid-navigation. An invite token is an explicit intent, so it still wins.
      const restorablePath = getRestorablePath({
        from: state?.from,
        previousUserId: state?.userId,
        currentUserId,
      });

      if (tokenData?.tokenType === TokenType.AccountClaim) {
        navigate(`/${paths.app}/${paths.chat}`);
      } else if (tokenData?.tokenType === TokenType.TeamCreate) {
        navigate({
          pathname: `/${paths.team_trial_invite}`,
          search,
        });
      } else if (tokenData?.tokenType === TokenType.Milestone) {
        // Milestone redirect is handled after successful acceptance in useSignIn hook
      } else if (restorablePath) {
        window.location.replace(restorablePath);
      } else if (user?.currentRole === Role.Volunteer) {
        navigate(`/${paths.select_account}`);
      } else {
        navigate(`/${paths.app}`);
      }
    }
  }, [user, hasHydrated, tokenData]);

  useEmailVerification();
  useInterruptedSignUp();

  const { loginWithCredentials, form, initialValues, loginWithCredentialsLoading } = useSignIn();
  const { PublicLogo, publicLogoProps, AdditionalLogo, additionalLogoProps } = getCurrentAppTheme();

  return (
    <>
      <div
        className="hidden sm:block pl-6 sm:pl-0 pt-16 sm:pt-0 rounded-bl-3xl rounded-br-3xl"
        style={{ backgroundColor: sm ? 'transparent' : bgColor }}
      >
        <Title className="text-base-100 mb-0 mt-0 sm:mt-auto">{t('sign_in_page.title')}</Title>
        <Text className="text-base-100">
          <Trans
            t={t}
            i18nKey="sign_in_page.dont_have_an_account"
            components={{
              b: (
                <Button
                  className="inline-flex h-auto p-0 text-base font-semibold text-base-100 underline underline-offset-4"
                  type="link"
                  onClick={() => navigate(`/${paths.sign_up}`)}
                />
              ),
            }}
          />
        </Text>
        {isYWC && <PublicLogo {...publicLogoProps} />}
      </div>
      <Card
        data-testid="login-form"
        className="border-none sm:border-solid w-full md:max-w-sm sm:mt-9 mb-4"
      >
        <div className="flex w-full self-start justify-between mb-4" ref={elementRef}>
          {isYWC ? (
            <YWCLogoSmall className="h-20 w-auto md:hidden" />
          ) : (
            <PublicLogo {...publicLogoProps} />
          )}
          <div className="flex items-center gap-2 ml-auto">
            <a
              href={t('faq.link')}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 md:hidden"
              style={{ color: textColorDark }}
            >
              {t('faq.text_short')}
              <QuestionCircleOutlined />
            </a>
            <AntdConfigProvider theme={{ components: { Select: baseSelect } }}>
              <AntdSelect
                variant="borderless"
                defaultValue={i18n.language}
                onChange={(value) => changeLanguage(value, i18n)}
                aria-label="Language"
                options={languageOptions()}
              />
            </AntdConfigProvider>
          </div>
        </div>
        <div className="sm:hidden">
          <Title className="text-base-100 mb-0 mt-0 sm:mt-auto" style={{ color: textColorDark }}>
            {t('sign_in_page.title')}
          </Title>
          <Text>
            <Trans
              t={t}
              i18nKey="sign_in_page.dont_have_an_account"
              components={{
                b: (
                  <Button
                    className="inline-flex h-auto p-0 text-base font-semibold underline underline-offset-4"
                    style={{ color: textColorDark }}
                    type="link"
                    onClick={() => navigate(`/${paths.sign_up}`)}
                  />
                ),
              }}
            />
          </Text>
        </div>
        <div className="flex flex-col items-center">
          <GoogleSignInButton width={elementWidth} />
        </div>
        <AntdDivider>
          <Text className="px-4 text-gray-500 text-center w-full text-base min-w-max">
            {t('sign_in_page.login_with_email')}
          </Text>
        </AntdDivider>
        <AntdForm
          form={form}
          layout="vertical"
          onFinish={loginWithCredentials}
          requiredMark={false}
          size={md ? 'middle' : 'large'}
          disabled={isFetching}
          initialValues={initialValues}
        >
          <AntdForm.Item
            label={t('common.email_field_label')}
            name="email"
            rules={[
              { required: true, message: t('validations.required') },
              () => ({
                validator(_, value) {
                  if (value && !isValidEmail(value))
                    return Promise.reject(new Error(t('validations.email')));
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input data-testid="email-input" className="h-[48px] sm:h-auto" inputMode="email" />
          </AntdForm.Item>

          <AntdForm.Item
            label={
              <>
                {t('common.password')}
                <a style={{ color: textColor }} href={paths.reset_password}>
                  {t('sign_in_page.forgot_password_link')}
                </a>
              </>
            }
            name="password"
            rules={[
              {
                message: t('validations.password'),
                pattern: passwordRegEx,
                required: true,
              },
            ]}
          >
            <InputPassword data-testid="password-input" className="h-[48px] sm:h-auto" />
          </AntdForm.Item>

          <Text
            className="mb-4 block text-sm italic leading-relaxed"
            style={{ color: textColorDark }}
          >
            <Trans
              t={t}
              i18nKey="sign_in_page.login_privacy_notice"
              components={[
                <ExternalLink
                  href={t('privacy_policy.link')}
                  key="privacy"
                  className="text-primary-600 underline"
                />,
              ]}
            />
          </Text>

          <Button
            className="w-full h-[48px] sm:h-auto"
            htmlType="submit"
            loading={loginWithCredentialsLoading}
            type="primary"
            data-testid="sign-in-button"
          >
            {loginWithCredentialsLoading ? '' : t('sign_in_page.title')}
          </Button>
        </AntdForm>
        <AdditionalLogo {...additionalLogoProps} />
      </Card>
    </>
  );
};

export default SignInPage;
